// @ts-nocheck
import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { AppDispatch } from '../../redux/store';
import { 
  fetchAIUsageScores, 
  selectAIUsageScoreIsLoading, 
  selectAIUsageScoreError,
  selectOrganizationSummary,
  selectUsers,
  setDateRange,
  selectDateRange
} from '../../redux/aiScore/aiUsageScoreSlice';
import Sidebar from '../../components/sidebar/Sidebar';
import { ResponsiveAppShell, TaskyHeaderActions } from '../../components/tasky';
import { format, subDays, startOfMonth, endOfMonth, startOfWeek, endOfWeek } from 'date-fns';
import { ja } from 'date-fns/locale';
import Image from '../../components/common/Image';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faBolt,
  faDownload,
} from '@fortawesome/free-solid-svg-icons';
import { exportUsageScoreToCSV } from '../../utils/csvExporter';
import { useToast } from '../../contexts/ToastContext';

// 日付タブの種類
type DateTabType = '今月' | '今週' | '今日' | 'カスタム';

const AIScorePage: React.FC = () => {
  const { showToast } = useToast();
  const dispatch = useDispatch<AppDispatch>();
  const isLoading = useSelector(selectAIUsageScoreIsLoading);
  const error = useSelector(selectAIUsageScoreError);
  const organizationSummary = useSelector(selectOrganizationSummary);
  const users = useSelector(selectUsers);
  const { startDate, endDate } = useSelector(selectDateRange);
  
  const [activeTab, setActiveTab] = useState<DateTabType>('今月');
  const [customStartDate, setCustomStartDate] = useState<string>(
    startDate || format(startOfMonth(new Date()), 'yyyy-MM-dd')
  );
  const [customEndDate, setCustomEndDate] = useState<string>(
    endDate || format(endOfMonth(new Date()), 'yyyy-MM-dd')
  );

  // 日付タブクリック時の処理
  const handleDateTabClick = (tab: DateTabType) => {
    setActiveTab(tab);
    
    let newStartDate: string;
    let newEndDate: string;
    
    const today = new Date();
    
    switch (tab) {
      case '今月':
        newStartDate = format(startOfMonth(today), 'yyyy-MM-dd');
        newEndDate = format(endOfMonth(today), 'yyyy-MM-dd');
        break;
      case '今週':
        newStartDate = format(startOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        newEndDate = format(endOfWeek(today, { weekStartsOn: 1 }), 'yyyy-MM-dd');
        break;
      case '今日':
        newStartDate = format(today, 'yyyy-MM-dd');
        newEndDate = format(today, 'yyyy-MM-dd');
        break;
      case 'カスタム':
        // カスタムの場合は現在の値を維持
        newStartDate = customStartDate;
        newEndDate = customEndDate;
        break;
      default:
        newStartDate = format(startOfMonth(today), 'yyyy-MM-dd');
        newEndDate = format(endOfMonth(today), 'yyyy-MM-dd');
    }
    
    dispatch(setDateRange({ startDate: newStartDate, endDate: newEndDate }));
  };

  // カスタム日付変更時の処理
  const handleCustomDateChange = (type: 'start' | 'end', value: string) => {
    if (type === 'start') {
      setCustomStartDate(value);
      if (activeTab === 'カスタム') {
        dispatch(setDateRange({ startDate: value, endDate }));
      }
    } else {
      setCustomEndDate(value);
      if (activeTab === 'カスタム') {
        dispatch(setDateRange({ startDate, endDate: value }));
      }
    }
  };

  // データ取得
  useEffect(() => {
    if (startDate && endDate) {
      dispatch(fetchAIUsageScores({ startDate, endDate }));
    }
  }, [dispatch, startDate, endDate]);

  // データをコンソールに出力
  useEffect(() => {
    if (organizationSummary) {
      console.log('AIスコアデータ:', { 
        組織サマリー: organizationSummary,
        よく使うエージェント: organizationSummary.top_agents,
        ユーザー一覧: users
      });
    }
  }, [organizationSummary, users]);

  // コンポーネントマウント時の初期データ取得
  useEffect(() => {
    // 初期値が未設定の場合は今月をデフォルトに
    if (!startDate || !endDate) {
      const today = new Date();
      const monthStart = format(startOfMonth(today), 'yyyy-MM-dd');
      const monthEnd = format(endOfMonth(today), 'yyyy-MM-dd');
      
      dispatch(setDateRange({ startDate: monthStart, endDate: monthEnd }));
    }
  }, [dispatch, startDate, endDate]);

  // ユーザーのイニシャルを取得
  const getUserInitials = (name: string): string => {
    if (!name) return '';
    
    const nameParts = name.split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[1].charAt(0)}`.toUpperCase();
    }
    
    return name.charAt(0).toUpperCase();
  };

  // ログアウト処理
  const handleLogout = async () => {
    console.log('AI活用スコアページ: ログアウト処理を開始');
    try {
      const { logout } = await import('../../redux/auth/authSlice');
      await dispatch(logout()).unwrap();
      console.log('ログアウト成功、ログインページへリダイレクト');
      window.location.href = '/login';
    } catch (error) {
      console.error('ログアウトエラー:', error);
      // エラーがあってもログインページへリダイレクト
      window.location.href = '/login';
    }
  };

  // CSVダウンロード処理
  const handleCSVDownload = () => {
    if (users.length === 0) {
      showToast('ダウンロードするデータがありません', 'error');
      return;
    }

    try {
      exportUsageScoreToCSV(users, startDate || '', endDate || '');
    } catch (error) {
      console.error('CSVダウンロードエラー:', error);
      showToast('CSVダウンロードに失敗しました', 'error');
    }
  };

  const sidebar = <Sidebar onLogout={handleLogout} />;

  return (
    <ResponsiveAppShell sidebar={sidebar} mobileSidebar={sidebar} topbarTitle="AI活用スコア">
      <div className="flex min-h-[calc(100dvh-56px)] flex-col bg-white lg:h-screen lg:min-h-0">
        {/* ページヘッダー */}
        <div className="hidden min-h-14 border-b border-warm-border items-center px-4 py-2 bg-white lg:flex">
          <div className="flex items-center justify-between gap-4 w-full">
            {/* 左側：タイトルセクション */}
            <div className="flex shrink-0 items-center space-x-3">
              <FontAwesomeIcon icon={faBolt} className="text-xl text-primary" />
              <div className="flex items-center space-x-2">
                <h1 className="m-0 whitespace-nowrap text-lg font-semibold text-dark">AI活用スコア</h1>
              </div>
            </div>

            {/* 右側：日付フィルター + CSVダウンロード */}
            <div className="flex flex-wrap items-center justify-end gap-3" data-tutorial="ai-score-date-filter">
              <div className="flex gap-2">
                <div
                  className={`py-1.5 px-3 rounded text-sm cursor-pointer transition-all ${
                    activeTab === '今月'
                      ? 'bg-[#616FC9] text-white'
                      : 'hover:bg-[#EDEBE4] text-gray-700'
                  }`}
                  onClick={() => handleDateTabClick('今月')}
                >
                  今月
                </div>
                <div
                  className={`py-1.5 px-3 rounded text-sm cursor-pointer transition-all ${
                    activeTab === '今週'
                      ? 'bg-[#616FC9] text-white'
                      : 'hover:bg-[#EDEBE4] text-gray-700'
                  }`}
                  onClick={() => handleDateTabClick('今週')}
                >
                  今週
                </div>
                <div
                  className={`py-1.5 px-3 rounded text-sm cursor-pointer transition-all ${
                    activeTab === '今日'
                      ? 'bg-[#616FC9] text-white'
                      : 'hover:bg-[#EDEBE4] text-gray-700'
                  }`}
                  onClick={() => handleDateTabClick('今日')}
                >
                  今日
                </div>
                <div
                  className={`py-1.5 px-3 rounded text-sm cursor-pointer transition-all ${
                    activeTab === 'カスタム'
                      ? 'bg-[#616FC9] text-white'
                      : 'hover:bg-[#EDEBE4] text-gray-700'
                  }`}
                  onClick={() => handleDateTabClick('カスタム')}
                >
                  カスタム
                </div>
              </div>

              <div className="flex items-center gap-2">
                <input
                  type="date"
                  className="h-8 py-1 px-2 border border-warm-border rounded text-xs w-28 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-[#616FC9]"
                  value={customStartDate}
                  onChange={(e) => handleCustomDateChange('start', e.target.value)}
                  disabled={activeTab !== 'カスタム'}
                />
                <span className="text-gray-400 text-sm">〜</span>
                <input
                  type="date"
                  className="h-8 py-1 px-2 border border-warm-border rounded text-xs w-28 disabled:opacity-50 disabled:cursor-not-allowed focus:outline-none focus:ring-1 focus:ring-[#616FC9]"
                  value={customEndDate}
                  onChange={(e) => handleCustomDateChange('end', e.target.value)}
                  disabled={activeTab !== 'カスタム'}
                />
              </div>

              {/* CSVダウンロードボタン */}
              <button
                className="flex items-center gap-2 py-1.5 px-4 bg-[#616FC9] text-white rounded text-sm hover:bg-[#4F5BB5] transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                onClick={handleCSVDownload}
                disabled={isLoading || users.length === 0}
                data-tutorial="ai-score-csv"
              >
                <FontAwesomeIcon icon={faDownload} />
                <span>CSVダウンロード</span>
              </button>
              <TaskyHeaderActions />
            </div>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto overflow-x-hidden bg-warm-card p-4 sm:p-6">
          
          {isLoading ? (
            <div className="loading">データを読み込み中...</div>
          ) : error ? (
            <div className="error-message mb-4 flex items-center justify-between border border-red-200">{error}</div>
          ) : (
            <>
              {/* メンバー別活用状況カード */}
              <div className="flex w-full flex-col gap-6 max-[640px]:gap-4" data-tutorial="ai-score-members">
                {users.map((user) => (
                  <div className="flex rounded-xl border border-warm-border bg-white p-6 shadow-[0_1px_3px_rgba(29,25,39,0.04)] max-[640px]:flex-col max-[640px]:p-4" key={user.id}>
                    {/* 左側：ユーザー情報 */}
                    <div className="flex min-w-[150px] flex-col items-center border-r border-warm-border pr-8 max-[640px]:w-full max-[640px]:min-w-0 max-[640px]:border-b max-[640px]:border-r-0 max-[640px]:pb-4 max-[640px]:pr-0">
                      <div className="flex flex-col items-center">
                        {user.avatar ? (
                          <img src={user.avatar} alt={user.name} className="mb-4 h-[60px] w-[60px] rounded-full object-cover" />
                        ) : (
                          <div className="mb-4 flex h-[60px] w-[60px] items-center justify-center rounded-full bg-[#EEF0FA] text-[1.2rem] font-semibold text-primary">{getUserInitials(user.name)}</div>
                        )}
                        <div className="mb-[0.2rem] text-center text-base font-medium">{user.name}</div>
                        <div className="text-center text-[0.8rem] text-white">{user.email}</div>
                      </div>
                    </div>

                    {/* 右側：よく使うAIエージェント */}
                    <div className="flex flex-1 flex-col pl-8 max-[640px]:w-full max-[640px]:min-w-0 max-[640px]:pl-0 max-[640px]:pt-4">
                      <div className="flex gap-4 max-[640px]:flex-col">
                        <div className="flex-1 rounded-[5px] bg-warm-surface p-[15px] max-[640px]:min-w-0">
                          <div className="mb-3 text-[0.9rem] font-medium text-gray-600">🤖 よく使うAIエージェント</div>
                          <div className="flex flex-col gap-3">
                            {user.used_agents.slice(0, 3).map((agent, index) => (
                              <div className="flex justify-between text-[0.85rem] max-[640px]:flex-col max-[640px]:items-start max-[640px]:gap-1" key={index}>
                                <div className="flex items-center font-medium text-dark">{index + 1}位: {agent.name}</div>
                                <div className="font-medium text-dark">
                                  {agent.count.toLocaleString()}回 ({agent.usage_rate}%)
                                </div>
                              </div>
                            ))}
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      </div>
    </ResponsiveAppShell>
  );
};

export default AIScorePage;
